import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuthContext } from '../context/AuthContext';
import { useActivePick } from '../hooks/useActivePick';
import { useGames } from '../hooks/useGames';
import { cancelPick, manualSyncGames } from '../services/pickService';
import GameCard from '../components/GameCard';
import GameFilters from '../components/GameFilters';
import Countdown from '../components/Countdown';
import LoadingSpinner from '../components/LoadingSpinner';
import { getPickStatusLabel, getNext3amET } from '../utils/formatters';
import { ROUTES, LEAGUES } from '../utils/constants';
import toast from 'react-hot-toast';
import './HomePage.css';

export default function HomePage() {
  const { isAuthenticated, userProfile } = useAuthContext();
  const { activePick, loading: pickLoading, refetch: refetchPick } = useActivePick();
  const { games: allGames, loading: gamesLoading, refetch } = useGames();
  const [searchParams, setSearchParams] = useSearchParams();
  const [syncing, setSyncing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [activePickGame, setActivePickGame] = useState(null);

  // Real-time listener for the active pick's game (for live score updates)
  useEffect(() => {
    if (!activePick?.gameId) {
      setActivePickGame(null);
      return;
    }
    const unsub = onSnapshot(doc(db, 'games', activePick.gameId), (snap) => {
      if (snap.exists()) {
        setActivePickGame({ id: snap.id, ...snap.data() });
      }
    });
    return () => unsub();
  }, [activePick?.gameId]);

  // Persist search & filter in URL so they survive navigate(-1)
  const activeFilter = searchParams.get('filter') || 'all';
  const searchQuery = searchParams.get('q') || '';

  const setActiveFilter = (filter) => {
    setSearchParams((prev) => {
      if (filter === 'all') prev.delete('filter');
      else prev.set('filter', filter);
      return prev;
    }, { replace: true });
  };

  const setSearchQuery = (query) => {
    setSearchParams((prev) => {
      if (!query) prev.delete('q');
      else prev.set('q', query);
      return prev;
    }, { replace: true });
  };

  const hasLockedPick = activePick?.status === 'locked';

  // Derive which leagues have games today
  const activeLeagueSlugs = useMemo(() => {
    const slugs = new Set();
    allGames.forEach((g) => slugs.add(g.leagueSlug));
    return slugs;
  }, [allGames]);

  // Build sport filter tabs — only show leagues with games
  const sportFilters = useMemo(() => {
    const filters = [{ key: 'all', label: 'All Sports' }];
    Object.values(LEAGUES).forEach((l) => {
      if (activeLeagueSlugs.has(l.slug)) {
        filters.push({ key: l.slug, label: `${l.emoji} ${l.name}` });
      }
    });
    return filters;
  }, [activeLeagueSlugs]);

  // Filter games by sport and search query
  const filteredGames = useMemo(() => {
    let result = allGames;

    // Sport filter
    if (activeFilter !== 'all') {
      result = result.filter((g) => g.leagueSlug === activeFilter);
    }

    // Search filter — match against team names and abbreviations
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (g) =>
          g.homeTeam.name.toLowerCase().includes(query) ||
          g.awayTeam.name.toLowerCase().includes(query) ||
          g.homeTeam.abbreviation.toLowerCase().includes(query) ||
          g.awayTeam.abbreviation.toLowerCase().includes(query)
      );
    }

    return result;
  }, [allGames, activeFilter, searchQuery]);

  // Reset filter if current filter has no games
  if (activeFilter !== 'all' && !activeLeagueSlugs.has(activeFilter)) {
    setActiveFilter('all');
  }

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await manualSyncGames();
      toast.success(result.message || 'Games synced!');
      await refetch();
    } catch (err) {
      console.error('Sync error:', err);
      toast.error('Failed to sync games.');
    } finally {
      setSyncing(false);
    }
  };

  const handleCancelPick = async () => {
    setCancelling(true);
    try {
      await cancelPick();
      await refetchPick();
      toast.success('Pick cancelled');
    } catch (err) {
      console.error('Cancel error:', err);
      toast.error(err.message || 'Failed to cancel pick.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="home-page">
      <div className="home-hero">
        {isAuthenticated && userProfile ? (
          <>
            <h1>
              Welcome back, <span className="highlight">{userProfile.username}</span>
            </h1>
            <div className="streak-display">
              <span className="streak-fire">🔥</span>
              <span className="streak-number">{userProfile.currentStreak || 0}</span>
              <span className="streak-label">Current Streak</span>
            </div>
          </>
        ) : (
          <>
            <h1>
              <span className="highlight">Streak for Cash</span>
            </h1>
            <p className="hero-subtitle">Pick winners, build your streak, compete for cash.</p>
            <Link to={ROUTES.LOGIN} className="btn btn-primary">
              Sign In to Play
            </Link>
          </>
        )}
      </div>

      {/* Active Pick Section — only for logged-in users */}
      {isAuthenticated && (
        <div className="home-section">
          {pickLoading ? (
            <LoadingSpinner message="Loading your pick..." />
          ) : activePick ? (
            <div className="active-pick-card">
              <h2>Your Active Pick</h2>
              <div className="active-pick-details">
                <div className="active-pick-team">
                  {activePick.pickTeamLogo && (
                    <img src={activePick.pickTeamLogo} alt="" className="active-pick-logo" />
                  )}
                  <div className="active-pick-info">
                    <span className="active-pick-name">{activePick.pickTeamName}</span>
                    <span className="active-pick-vs">vs {activePick.opponentTeamName}</span>
                    <span className="active-pick-league">
                      {LEAGUES[activePick.leagueSlug]?.emoji} {activePick.league}
                    </span>
                  </div>
                </div>
                <div className="active-pick-status">
                  {activePickGame && (activePickGame.status === 'in_progress' || activePickGame.status === 'final') ? (
                    <>
                      <span className="active-pick-score">
                        {activePickGame.awayTeam?.abbreviation} {activePickGame.awayScore ?? 0}
                        {' '}&ndash;{' '}
                        {activePickGame.homeTeam?.abbreviation} {activePickGame.homeScore ?? 0}
                      </span>
                      {activePickGame.statusDetail &&
                        !activePickGame.statusDetail.toLowerCase().includes('scheduled') && (
                        <span className="active-pick-time">{activePickGame.statusDetail}</span>
                      )}
                    </>
                  ) : activePick.status === 'pending' ? (
                    <>
                      <span className="countdown-label">Starts in</span>
                      <Countdown targetTime={activePick.gameStartTime} />
                    </>
                  ) : (
                    <span className={`status-badge status-${activePick.status}`}>
                      {getPickStatusLabel(activePick.status)}
                    </span>
                  )}
                </div>
              </div>
              {activePick.status === 'pending' && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleCancelPick}
                  disabled={cancelling}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Pick'}
                </button>
              )}
            </div>
          ) : (
            <div className="no-pick-card">
              <h2>No Active Pick</h2>
              <p>Pick a winner to start building your streak!</p>
              <Link to={ROUTES.GAMES} className="btn btn-primary">
                Browse Games
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Upcoming Games */}
      <div className="home-section">
        <div className="section-header">
          <h2>Upcoming Games</h2>
          <div className="section-header-actions">
            <button
              className="btn btn-secondary btn-sm sync-btn"
              onClick={handleSync}
              disabled={syncing}
              title="Sync Games"
            >
              <svg
                className={`sync-icon ${syncing ? 'spinning' : ''}`}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21.5 2v6h-6" />
                <path d="M2.5 22v-6h6" />
                <path d="M2.5 11.5a10 10 0 0 1 18.4-4.5" />
                <path d="M21.5 12.5a10 10 0 0 1-18.4 4.5" />
              </svg>
            </button>
          </div>
        </div>

        {gamesLoading ? (
          <LoadingSpinner message="Loading games..." />
        ) : allGames.length === 0 ? (
          <div className="no-games-card">
            <p>All games for today have finished. Check back tomorrow for a new slate!</p>
            <div className="next-games-countdown">
              <span className="next-games-label">New games in</span>
              <Countdown targetTime={getNext3amET()} onComplete={refetch} />
            </div>
          </div>
        ) : (
          <>
            <GameFilters
              sportFilters={sportFilters}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              gameCount={filteredGames.length}
            />

            {filteredGames.length === 0 ? (
              <div className="no-games-card">
                <p>No games match your search.</p>
              </div>
            ) : (
              <div className="games-preview-list">
                {filteredGames.map((game) => (
                  <GameCard
                    key={game.gameId}
                    game={game}
                    activePick={activePick}
                    disabled={hasLockedPick}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
